import { useState, useEffect } from "react";
import { cn } from "../../../utils/utils";
import { cva, type VariantProps } from "class-variance-authority";
import Spinner from "../../../components/spinner.components";
import { Link } from "react-router-dom";

const buttonVariants = cva(
  "relative px-12 py-4 border rounded-xl font-normal text-lg cursor-pointer ease-in-out",
  {
    variants: {
      variant: {
        primary:
          "bg-[#A6FF4D] text-black border-black transition-all duration-300",
        secondary:
          "bg-white text-black border-black transition-all duration-300",
        solid: "bg-white text-black border-black cursor-default pointer-events-none",
        yellow: "bg-[#FFD742] text-black border-black ",
        boost:"bg-soloboost text-white border-black",
        red:"bg-red-100 text-red-500 border-black transition-all duration-300",
        danger:"bg-red-500 text-white border-black transition-all duration-300",
        outline: "bg-white text-black border-black  border-[1px] ",
      },
      size: {
        default: "px-12 py-4 text-lg",
        sm: "px-8 py-2 text-base",
        lg: "px-16 py-6 text-xl",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  label?: string;
  isLoading?: boolean;
  disabled?: boolean;
  spinnerSize?: number;
  to?: string;
  target?: string;
  asLink?: boolean;
}

export default function Button({
  label,
  onClick,
  isLoading,
  disabled,
  variant,
  size,
  className,
  spinnerSize = 20,
  to,
  target,
  asLink,
  ...props
}: ButtonProps) {
  const [isPressed, setIsPressed] = useState(false);
  const isDisabled = disabled || isLoading;

  // Update isPressed state when isLoading changes
  useEffect(() => {
    if (isLoading) {
      setIsPressed(true);
    } else {
      // Add a small delay before releasing the pressed state for a smooth transition
      const timer = setTimeout(() => {
        setIsPressed(false);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  const buttonContent = (
    <div className="flex justify-center items-center min-w-full min-h-full font-cabinetgrotesk">
      {isLoading ? (
        <div className="py-1">
          <Spinner width={spinnerSize} className="w-3 md:w-4"  />
        </div>
      ) : (
        <>
          {label}
          {props.children}
        </>
      )}
    </div>
  );

  const buttonClasses = cn(
    buttonVariants({ variant, size, className }),
    ((isPressed && !disabled) || isLoading) &&
      variant !== "solid" &&
      variant !== "outline"
      ? "translate-x-0.5 translate-y-0.5"
      : "",
    isDisabled
      ? "opacity-70 cursor-not-allowed text-[#898989] border-[#898989]"
      : ""
  );

  const handleMouseEvents =
    variant === "solid" || variant === "outline"
      ? {}
      : {
          onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
            !isDisabled && setIsPressed(true);
            setTimeout(() => {
              setIsPressed(false);
              !isDisabled && setIsPressed(false);
              // biome-ignore lint/complexity/useOptionalChain: <explanation>
              onClick && onClick(e);
            }, 200);
          },
        };

  // Render as Link if to prop is provided or asLink is true
  if ((to || asLink) && !isDisabled) {
    return (
      <div className="relative flex">
        {/* Background Shadow */}
        <div
          className={`top-0.5 left-0.5 absolute bg-black rounded-xl w-full h-full ${className}`}
        />

        <Link
          to={to || "#"}
          target={target}
          className={cn(buttonClasses, "flex items-center justify-center")}
          // biome-ignore lint/suspicious/noExplicitAny: <explanation>
          onClick={(e) => onClick?.(e as any)}
        >
          {buttonContent}
        </Link>
      </div>
    );
  }

  // Otherwise render as button
  return (
    <div className="relative flex">
      {/* Background Shadow - only show if not disabled */}
      {!isDisabled && variant !== "outline" && (
        <div
          className={`top-0.5 left-0.5 absolute bg-black rounded-xl w-full h-full ${className}`}
        />
      )}

      {/* Button */}
      <button
        onClick={onClick}
        disabled={isDisabled}
        className={buttonClasses}
        {...handleMouseEvents}
        {...props}
      >
        {buttonContent}
      </button>
    </div>
  );
}
